import resolvedEndpoints from '../../constants/endpoints';
import { getHttpInstance } from '../../services/defaultHttpService';
import * as types from './constants';
import { normalizeError } from '../exception/utils';
import {
  ERROR_DOMAIN,
  ERROR_PATH,
  MLP_ERROR_MESSAGE,
  PMKIT_ERROR_MESSAGE
} from '../../constants/errorConstants';
import { setError, clearError } from '../exception/actions';
import { replaceTokensInString, isEmpty } from '../../utils';
import {
  NO_CACHE_HEADER,
  TIMEOUT_EXTENDED
} from '../../constants/commonConstants';
import { normalizeServerError } from './utils';
import { replaceTokensInUrl } from '@app/utils';
import { replaceFromString } from '@app/utils/stringUtils';

const {
  MACHINE_DETAILS_LUCID,
  LOAD_MACHINE_DETAILS,
  LOAD_SSRO_URLFORSERIALNUMBER,
  LOAD_SSRO_URLFORMODEL,
  PMKIT_EQUIPMENT_DETAILS_URL,
  EQUIPMENT_SPECIFIC_PART_CATEGORIES,
  BEST_SELLER
} = resolvedEndpoints;

export const loadMachineDetails =
  (isMachineDetailsFlag, serialNumber = '', salesModel = '') =>
  (dispatch, getState) => {
    const http = getHttpInstance();
    const {
      locale,
      langId,
      catalogId,
      storeId,
      storeIdentifier,
      featureDetails
    } = getState().common;
    const featureValues = featureDetails.data ?? {};
    const { current: productId } = getState().products;
    dispatch({ type: types.MLP_LOAD_EQUIPMENTDETAILS_BEGIN });

    const urlParams = isMachineDetailsFlag
      ? { serialNumber, locale, storeIdentifier, salesModel, langId }
      : {
          langId,
          catalogId,
          storeId,
          productId,
          serialNumber
        };
    const url = isMachineDetailsFlag
      ? replaceTokensInString(MACHINE_DETAILS_LUCID, storeId)
      : LOAD_MACHINE_DETAILS;
    const config = { headers: NO_CACHE_HEADER };
    return http
      .get(url, { params: urlParams }, config)
      .then(response => {
        if (response.data.errors) {
          const err = normalizeServerError(response);
          dispatch(
            setError(ERROR_DOMAIN.MLP, ERROR_PATH.EQUIPMENT_DETAILS, err)
          );
          dispatch({ type: types.MLP_LOAD_EQUIPMENTDETAILS_FAIL }, response);
        } else {
          const { equipmentDetails } = response.data;
          if (isMachineDetailsFlag) {
            const { model, name } = equipmentDetails;
            equipmentDetails.family = replaceFromString(model, name);
          }
          if (
            equipmentDetails.categories &&
            equipmentDetails.code === types.ELECTRIC_MACHINE_CODE
          ) {
            equipmentDetails.categories = equipmentDetails.categories.filter(
              part =>
                !featureValues.PCC_ALP_NON_RELATED_CATEGORIES.includes(
                  part.identifier
                )
            );
          }
          dispatch({
            type: types.MLP_LOAD_EQUIPMENTDETAILS_SUCCESS,
            payload: {
              equipmentDetails: equipmentDetails
            }
          });
        }
      })
      .catch(payload => {
        const err = normalizeError(payload, MLP_ERROR_MESSAGE);
        dispatch(setError(ERROR_DOMAIN.MLP, ERROR_PATH.EQUIPMENT_DETAILS, err));
        dispatch({ type: types.MLP_LOAD_EQUIPMENTDETAILS_FAIL }, payload);
      });
  };

export const loadPartsByCategory =
  (serialNumber, salesModel, subCategoryLimit = 0) =>
  (dispatch, getState) => {
    const http = getHttpInstance(TIMEOUT_EXTENDED);
    const { code } = getState().mlp.equipmentDetails.data ?? '';
    const { langId, storeId, featureDetails } = getState().common;
    const featureValues = featureDetails.data ?? {};

    dispatch({ type: types.MLP_LOAD_CATEGORIES_BEGIN });
    const urlParams = {
      langId,
      serialNumber,
      salesModel,
      subCategoryLimit
    };
    const url = replaceTokensInUrl(EQUIPMENT_SPECIFIC_PART_CATEGORIES, storeId);
    const config = { headers: NO_CACHE_HEADER };
    return http
      .get(url, { params: urlParams }, config)
      .then(response => {
        let { categories, totalCount } =
          response.data.categoriesForSelectedEquipment;
        if (code === types.ELECTRIC_MACHINE_CODE) {
          categories =
            response.data.categoriesForSelectedEquipment.categories.filter(
              part =>
                !featureValues.PCC_ALP_NON_RELATED_CATEGORIES.includes(
                  part.identifier
                )
            );
          totalCount = categories.length;
        }
        dispatch({
          type: types.MLP_LOAD_CATEGORIES_SUCCESS,
          payload: {
            partsByCategory: categories,
            totalCount: totalCount
          }
        });
      })
      .catch(payload => {
        const err = normalizeError(payload, MLP_ERROR_MESSAGE);
        dispatch(setError(ERROR_DOMAIN.MLP, ERROR_PATH.PARTS_BY_CATEGORY, err));
        dispatch({ type: types.MLP_LOAD_CATEGORIES_FAIL }, payload);
      });
  };

export const loadTopSellingPartsInfo =
  (salesModel, category, serialNumber = '') =>
  (dispatch, getState) => {
    const http = getHttpInstance();
    const { storeIdentifier, langId, storeId } = getState().common;
    dispatch({ type: types.MLP_LOAD_TOPSELLINGPARTS_BEGIN });
    const url = replaceTokensInUrl(
      BEST_SELLER,
      langId,
      storeId,
      storeIdentifier,
      salesModel,
      category,
      serialNumber
    );
    return http
      .get(url)
      .then(response => {
        const parts = response.data?.BestSellers ?? [];
        dispatch({
          type: types.MLP_LOAD_TOPSELLINGPARTS_SUCCESS,
          payload: {
            topSellingParts: {
              topSellingParts: parts,
              viewMore: '',
              totalCount: parts.length
            }
          }
        });
      })
      .catch(payload => {
        dispatch({ type: types.MLP_LOAD_TOPSELLINGPARTS_FAIL }, payload);
      });
  };
const dispatchSsroError = (payload, dispatch, error) => {
  dispatch(
    setError(ERROR_DOMAIN.MLP, ERROR_PATH.SELF_SERVICE_REPAIR_OPTIONS, error)
  );
  dispatch({ type: types.MLP_LOAD_SSRO_FAIL }, payload);
};

const handleSsroServerError = (payload, dispatch) => {
  const err = normalizeServerError(payload);
  dispatchSsroError(payload, dispatch, err);
};

const handleSsroClientError = (payload, dispatch) => {
  const err = normalizeError(payload, MLP_ERROR_MESSAGE);
  dispatchSsroError(payload, dispatch, err);
};

export const loadSSROBySerial =
  (serialNumber, salesModel, isPM, isCE) => (dispatch, getState) => {
    const http = getHttpInstance();
    const {
      common: { langId, catalogId, storeId },
      mlp: { equipmentList },
      products: { current: productId }
    } = getState();
    const { prefix, low, high } =
      typeof serialNumber === 'number' &&
      equipmentList.data.models[serialNumber]?.serialNumberOrPrefix;
    serialNumber = typeof serialNumber === 'number' ? prefix : serialNumber;
    const config = { headers: NO_CACHE_HEADER };
    if (isPM) {
      dispatch({ type: types.MLP_LOAD_SSRO_PM_KIT_BEGIN });
    } else {
      dispatch({ type: types.MLP_LOAD_SSRO_BEGIN });
    }
    const urlParams = {
      productId,
      langId,
      catalogId,
      serialNumber,
      salesModel,
      serialNoBegin: low,
      serialNoEnd: high,
      isPM,
      isCE
    };
    const url = replaceTokensInString(LOAD_SSRO_URLFORSERIALNUMBER, storeId);
    return http
      .get(url, { params: urlParams }, config)
      .then(response => {
        if (response.data.errors) {
          handleSsroServerError(response, dispatch);
        } else {
          if (response.data.bundles?.length) {
            dispatch({
              type: types.MLP_LOAD_SSRO_SERIAL_SUCCESS,
              payload: {
                bundleData: response.data
              }
            });
          } else {
            dispatch({
              type: types.MLP_LOAD_SSRO_FAIL,
              payload: { isPMKit: isPM }
            });
          }
        }
      })
      .catch(payload => {
        if (isPM) {
          const err = normalizeError(payload, MLP_ERROR_MESSAGE);
          dispatch(
            setError(
              ERROR_DOMAIN.MLP,
              ERROR_PATH.SELF_SERVICE_REPAIR_OPTIONS,
              err
            )
          );
          dispatch({
            type: types.MLP_LOAD_SSRO_FAIL,
            payload: { isPMKit: isPM }
          });
        } else {
          handleSsroClientError(payload, dispatch);
        }
      });
  };

export const loadSSROByModel = (model, isPM) => (dispatch, getState) => {
  const http = getHttpInstance();
  const { langId, catalogId, storeId } = getState().common;
  const { current: productId } = getState().products;
  const config = { headers: NO_CACHE_HEADER };
  if (isPM) {
    dispatch({ type: types.MLP_LOAD_SSRO_PM_KIT_BEGIN });
  } else {
    dispatch({ type: types.MLP_LOAD_SSRO_BEGIN });
  }

  const urlParams = {
    productId,
    langId,
    catalogId
  };
  const url = replaceTokensInString(
    LOAD_SSRO_URLFORMODEL,
    storeId,
    encodeURIComponent(model)
  );
  return http
    .get(url, { params: urlParams }, config)
    .then(response => {
      if (response.data.errors) {
        handleSsroServerError(response, dispatch);
      } else {
        const { ssroBundlesExist } = response.data;
        dispatch({
          type: types.MLP_LOAD_SSRO_MODEL_SUCCESS,
          payload: {
            ssroBundlesExist: ssroBundlesExist,
            bundleData: { bundles: [] }
          }
        });
      }
    })
    .catch(payload => {
      handleSsroClientError(payload, dispatch);
    });
};

export const loadSSROOptions =
  (runDispatch, serialNumber, model) => (dispatch, getState) => {
    if (runDispatch && serialNumber && model) {
      dispatch(loadSSROBySerial(serialNumber, model)).then(() => {
        const { ssroBundlesExist, bundleData } =
          getState().mlp.ssroOptions.data;
        if (
          (ssroBundlesExist === null || ssroBundlesExist === undefined) &&
          isEmpty(bundleData)
        ) {
          dispatch(loadSSROByModel(model));
        }
      });
    } else if (runDispatch && model) {
      dispatch(loadSSROByModel(model));
    } else if (isEmpty(model)) {
      handleSsroServerError({}, dispatch);
    }
  };

export const loadPMKitEquipmentDetails =
  (equipmentModel = null) =>
  (dispatch, getState) => {
    const http = getHttpInstance();
    const {
      common: { storeId, langId },
      myEquipment: { equipments }
    } = getState();
    const salesModel = equipmentModel ?? equipments.selectedEquipment.model;
    dispatch({ type: types.PMKIT_EQUIPMENTDETAILS_BEGIN });
    const url = replaceTokensInString(
      PMKIT_EQUIPMENT_DETAILS_URL,
      storeId,
      salesModel,
      langId
    );
    return http
      .get(url)
      .then(response => {
        if (response.data.errors) {
          const err = normalizeServerError(response);
          dispatch(setError(ERROR_DOMAIN.MLP, ERROR_PATH.EQUIPMENT_LIST, err));
          dispatch({ type: types.PMKIT_EQUIPMENTDETAILS_FAIL });
        } else {
          if (response.data.equipment?.models?.length) {
            dispatch(clearError(ERROR_DOMAIN.MLP, ERROR_PATH.EQUIPMENT_LIST));
            dispatch({
              type: types.PMKIT_EQUIPMENTDETAILS_SUCCESS,
              payload: response.data.equipment
            });
          } else {
            dispatch({ type: types.PMKIT_EQUIPMENTDETAILS_FAIL });
          }
        }
      })
      .catch(payload => {
        const err = normalizeError(payload, PMKIT_ERROR_MESSAGE);
        dispatch(setError(ERROR_DOMAIN.MLP, ERROR_PATH.EQUIPMENT_LIST, err));
        dispatch({ type: types.PMKIT_EQUIPMENTDETAILS_FAIL });
      });
  };

export default {
  loadMachineDetails,
  loadPartsByCategory,
  loadTopSellingPartsInfo,
  loadSSROBySerial,
  loadSSROByModel,
  loadSSROOptions,
  loadPMKitEquipmentDetails
};
